import { Component, type ErrorInfo, type ReactNode } from "react";
import { Button } from "@/client/components/ui/button";

// App-root error boundary (defense in depth). React has no hook equivalent for error boundaries,
// so this stays a class component. Any render/lifecycle throw anywhere below it degrades to a
// recoverable screen instead of a frozen or blank portal. "Opnieuw proberen" clears the caught
// error so the tree re-mounts; "Pagina herladen" is the hard fallback when re-render alone is not
// enough. Copy is warm and in Dutch, no em dashes (project rule).
type Props = { children: ReactNode };
type State = { hasError: boolean };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface it in the console for diagnosis; we have no client error-reporting sink yet.
    console.error("Unhandled render error", error, info.componentStack);
  }

  private handleReset = () => {
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="mx-auto max-w-md px-6 text-center">
          <h1 className="text-lg font-bold text-foreground">Er ging iets mis</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Er is een onverwachte fout opgetreden. Probeer het opnieuw; uw eerder opgeslagen gegevens
            blijven bewaard.
          </p>
          <div className="mt-4 flex items-center justify-center gap-3">
            <Button size="lg" onClick={this.handleReset}>
              Opnieuw proberen
            </Button>
            <Button size="lg" variant="outline" onClick={() => window.location.reload()}>
              Pagina herladen
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
